import { useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { useRecoilValue } from 'recoil';
import { SymbolGroup } from 'types/canvas';
import { SGTooltip } from 'components/core/SGTooltip';
import { hiroSymbolsState } from 'states/hiro';
import CanvasSymSelector from 'components/canvas/components/CanvasSymSelector';
import { useCanvasGroupCtx } from './CanvasGroupContext';
import { EditGroupDialog } from './EditGroupDialog';
import { HideSupportStyle } from 'components/shared/HideSupportStyle';

export interface ManageGroupsDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Workspace-level dialog: list every symbol group with its color swatch,
 * name, member count, and inline rename / delete affordances. The bottom
 * "+ New group" button opens the same EditGroupDialog used for editing.
 */
export const ManageGroupsDialog = ({
  open,
  onClose,
}: ManageGroupsDialogProps) => {
  const theme = useTheme();
  const {
    groups,
    createGroup,
    renameGroup,
    recolorGroup,
    deleteGroup,
    pickDefaultColor,
    getGroupSym,
    broadcastSymToGroup,
  } = useCanvasGroupCtx();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const hiroSymbols = useRecoilValue(hiroSymbolsState);
  const symOptions = useMemo(
    () =>
      Array.from(hiroSymbols)
        .sort()
        .map((s) => ({ symbol: s })),
    [hiroSymbols],
  );

  const [editingGroup, setEditingGroup] = useState<SymbolGroup | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SymbolGroup | null>(null);

  const closeEdit = () => setEditingGroup(null);
  const closeCreate = () => setCreateOpen(false);
  const cancelDelete = () => setPendingDelete(null);
  const confirmDelete = async () => {
    if (pendingDelete == null) {
      return;
    }
    const groupId = pendingDelete.id;
    setPendingDelete(null);
    await deleteGroup(groupId);
  };

  return (
    <>
      {open && <HideSupportStyle />}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 2.5,
          },
        }}
      >
        <DialogTitle>Symbol groups</DialogTitle>
        <Stack sx={{ ml: '24px', mb: 0.5 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 13, lineHeight: 1.5 }}
          >
            Group canvas components together so a symbol change on any one syncs
            to every member of the group.
          </Typography>
        </Stack>

        <DialogContent>
          {groups.length === 0 ? (
            <Stack
              alignItems="center"
              sx={{
                py: 5,
                px: 3,
                textAlign: 'center',
                gap: 2,
                borderRadius: 2,
                border: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
                backgroundColor: alpha(theme.palette.background.default, 0.4),
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                }}
              >
                <WorkspacesIcon sx={{ fontSize: 28 }} />
              </Box>
              <Stack gap={0.5} alignItems="center">
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, fontSize: 15 }}
                >
                  No groups yet
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 340, fontSize: 13, lineHeight: 1.5 }}
                >
                  Create your first group to keep symbols in sync across
                  multiple components.
                </Typography>
              </Stack>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                Create your first group
              </Button>
            </Stack>
          ) : (
            <Stack sx={{ gap: 3 }}>
              {groups.map((group) => {
                const memberCount = group.componentIds.length;
                return (
                  <Stack
                    key={group.id}
                    sx={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 3,
                      p: 3,
                      borderRadius: 1.5,
                      border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                      backgroundColor: alpha(
                        theme.palette.background.default,
                        0.3,
                      ),
                      transition: 'background-color 120ms, border-color 120ms',
                      '&:hover': {
                        borderColor: alpha(group.color, 0.5),
                        backgroundColor: alpha(group.color, 0.04),
                        '& .group-actions': { opacity: 1 },
                      },
                    }}
                  >
                    <SGTooltip title="Click to recolor or rename">
                      <IconButton
                        size="small"
                        onClick={() => setEditingGroup(group)}
                        aria-label={`Edit ${group.name}`}
                        sx={{
                          color: group.color,
                          flexShrink: 0,
                          transition: 'background-color 120ms, transform 120ms',
                          '&:hover': {
                            backgroundColor: alpha(group.color, 0.12),
                            transform: 'scale(1.08)',
                          },
                        }}
                      >
                        <WorkspacesIcon fontSize="small" />
                      </IconButton>
                    </SGTooltip>

                    <Stack gap={0.25} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: theme.palette.text.primary,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: 1.3,
                        }}
                      >
                        {group.name}
                      </Typography>
                      <Typography
                        component="div"
                        variant="caption"
                        sx={{
                          fontSize: 12,
                          color: theme.palette.text.secondary,
                          lineHeight: 1.3,
                        }}
                      >
                        {memberCount === 0
                          ? 'No components yet'
                          : `${memberCount} linked ${
                              memberCount === 1 ? 'component' : 'components'
                            }`}
                      </Typography>
                    </Stack>

                    <Box
                      sx={{
                        flexShrink: 0,
                        opacity: memberCount > 0 ? 1 : 0.55,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <CanvasSymSelector
                        sym={getGroupSym(group.id) ?? ''}
                        options={symOptions}
                        onSymChange={(sym) =>
                          broadcastSymToGroup(group.id, sym)
                        }
                      />
                    </Box>

                    <Stack
                      direction="row"
                      gap={0.5}
                      className="group-actions"
                      sx={{
                        opacity: 0.55,
                        transition: 'opacity 120ms',
                      }}
                    >
                      <SGTooltip title="Rename / recolor">
                        <IconButton
                          size="small"
                          onClick={() => setEditingGroup(group)}
                          sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              color: theme.palette.primary.main,
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.08,
                              ),
                            },
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </SGTooltip>

                      <SGTooltip title="Delete group">
                        <IconButton
                          size="small"
                          onClick={() => setPendingDelete(group)}
                          sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              color: theme.palette.error.main,
                              backgroundColor: alpha(
                                theme.palette.error.main,
                                0.08,
                              ),
                            },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </SGTooltip>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          {groups.length > 0 ? (
            <Button
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              New group
            </Button>
          ) : (
            <Box />
          )}
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              textTransform: 'none',
              minWidth: 80,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create */}
      <EditGroupDialog
        open={createOpen}
        title="New symbol group"
        initialName=""
        initialColor={pickDefaultColor()}
        onSubmit={(name, color) => {
          closeCreate();
          void createGroup(name, color);
        }}
        onClose={closeCreate}
      />

      {/* Edit */}
      <EditGroupDialog
        open={editingGroup != null}
        title="Edit symbol group"
        initialName={editingGroup?.name ?? ''}
        initialColor={editingGroup?.color ?? '#42a5f5'}
        onSubmit={(name, color) => {
          if (editingGroup == null) {
            return;
          }
          const groupId = editingGroup.id;
          closeEdit();
          // Two writes in parallel — backend handles either order fine. We
          // skip the second if nothing changed to avoid a wasted PUT.
          const tasks: Promise<unknown>[] = [];
          if (name !== editingGroup.name) {
            tasks.push(renameGroup(groupId, name));
          }
          if (color.toLowerCase() !== editingGroup.color.toLowerCase()) {
            tasks.push(recolorGroup(groupId, color));
          }
          void Promise.all(tasks);
        }}
        onClose={closeEdit}
      />

      {/* Delete confirmation */}
      <Dialog
        open={pendingDelete != null}
        onClose={cancelDelete}
        maxWidth="xs"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 2.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17, pb: 1, pt: 2.5 }}>
          Delete this group?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ fontSize: 13.5, lineHeight: 1.6 }}>
            <Box
              component="span"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              "{pendingDelete?.name}"
            </Box>{' '}
            will be removed. Linked components will keep their last symbol but
            won't stay in sync anymore. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={cancelDelete}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={pendingDelete == null}
            onClick={() => void confirmDelete()}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
